import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAdmin, getFullName, type AdminUser } from '../../../hooks/useAdmin';
import { useToast } from '../../../components/common/ToastProvider';
import adminService from '../../../services/adminService';

// Tier colors for subscription badges
const getTierColors = (planSlug?: string) => {
  if (!planSlug) return { bg: 'bg-gray-100', text: 'text-gray-600' };
  const slug = planSlug.toLowerCase();
  if (slug.includes('basic') || slug.includes('individual') || slug.includes('rent')) {
    return { bg: 'bg-[#E2F6FD]', text: 'text-[#0B5E78]' };
  }
  if (slug.includes('professional') || slug.includes('broker')) {
    return { bg: 'bg-[var(--pv-color-surface-sunken)]', text: 'text-[#003930]' };
  }
  if (slug.includes('company') || slug.includes('enterprise')) {
    return { bg: 'bg-[#FFF4D6]', text: 'text-[#4A3800]' };
  }
  return { bg: 'bg-[var(--pv-color-surface-sunken)]', text: 'text-[#003930]' };
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'CANCELLED': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'EXPIRED': return 'bg-rose-100 text-rose-700 border-rose-200';
    case 'PENDING': return 'bg-blue-100 text-blue-700 border-blue-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

const getFalBadgeClasses = (status?: 'PENDING' | 'APPROVED' | 'REJECTED') => {
  switch (status) {
    case 'APPROVED': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'REJECTED': return 'bg-rose-100 text-rose-700 border-rose-200';
    case 'PENDING':
    default:
      return 'bg-amber-100 text-amber-700 border-amber-200';
  }
};

export default function AdminUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { users, setUsers, plans, pagination, loading, error, fetchUsers, clearError, grantSubscription, updateSubscription, cancelSubscription } = useAdmin();
  const { showToast } = useToast();
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // FAL reject dialog state
  const [rejectDialogUser, setRejectDialogUser] = useState<AdminUser | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [falLoading, setFalLoading] = useState<string | null>(null); // agentId being updated

  // Filter state
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [planFilter, setPlanFilter] = useState(searchParams.get('plan') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch users when filters change
  useEffect(() => {
    fetchUsers({
      page: currentPage,
      pageSize: 20,
      search: debouncedSearch || undefined,
      subscriptionStatus: statusFilter || undefined,
      planType: planFilter || undefined
    });

    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter) params.set('status', statusFilter);
    if (planFilter) params.set('plan', planFilter);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [debouncedSearch, statusFilter, planFilter, currentPage, fetchUsers, setSearchParams]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPlanFilter('');
    setCurrentPage(1);
  };

  const hasFilters = search || statusFilter || planFilter;

  // FAL status update handler
  const handleFalStatusUpdate = useCallback(async (
    user: AdminUser,
    status: 'PENDING' | 'APPROVED' | 'REJECTED',
    rejectionReason?: string
  ) => {
    const agentId = user.agentId;
    if (!agentId) {
      showToast('This user does not have an agent profile.', 'error');
      return;
    }

    setFalLoading(agentId);
    try {
      await adminService.updateAgentFalStatus(agentId, status, rejectionReason);

      // Optimistic update: update in local state
      if (setUsers) {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === user.id
              ? { ...u, falVerificationStatus: status, falRejectionReason: rejectionReason }
              : u
          )
        );
      }

      const toastMap = {
        APPROVED: 'FAL license approved successfully',
        REJECTED: 'FAL license rejected',
        PENDING: 'FAL license revoked (set to pending)',
      };
      showToast(toastMap[status], status === 'APPROVED' ? 'success' : status === 'REJECTED' ? 'error' : 'info');

      if (status === 'REJECTED') {
        setRejectDialogUser(null);
        setRejectReason('');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update FAL status';
      showToast(message, 'error');
    } finally {
      setFalLoading(null);
    }
  }, [showToast, setUsers]);

  const handleApprove = useCallback((user: AdminUser) => {
    void handleFalStatusUpdate(user, 'APPROVED');
  }, [handleFalStatusUpdate]);

  const handleRevoke = useCallback((user: AdminUser) => {
    void handleFalStatusUpdate(user, 'PENDING');
  }, [handleFalStatusUpdate]);

  const handleRejectConfirm = useCallback(() => {
    if (!rejectDialogUser || !rejectReason.trim()) return;
    void handleFalStatusUpdate(rejectDialogUser, 'REJECTED', rejectReason.trim());
  }, [rejectDialogUser, rejectReason, handleFalStatusUpdate]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#004D40] shadow-lg shadow-[#004D40]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin"
                className="flex items-center justify-center w-11 h-11 rounded-2xl bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all duration-300 hover:scale-105"
              >
                <i className="ri-arrow-left-line text-white text-lg"></i>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white">User Management</h1>
                <p className="text-[#B7DDD7] mt-1 flex items-center gap-2">
                  <i className="ri-group-line"></i>
                  {pagination.total} total users
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-2xl text-white/90 text-sm">
                <i className="ri-shield-check-line mr-2"></i>
                Admin Panel
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-[#004D40]/5 border border-[var(--pv-border-soft)]/50 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-[var(--pv-color-surface-sunken)] rounded-xl flex items-center justify-center">
                  <i className="ri-search-line text-[#004D40]"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-14 pr-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] transition-all duration-300 bg-[var(--pv-color-surface-sunken)]/30"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full md:w-52">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] bg-white appearance-none cursor-pointer transition-all duration-300"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239333ea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="EXPIRED">Expired</option>
                <option value="PENDING">Pending</option>
                <option value="none">No Subscription</option>
              </select>
            </div>

            {/* Plan Filter */}
            <div className="w-full md:w-52">
              <select
                value={planFilter}
                onChange={(e) => {
                  setPlanFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-4 border-2 border-[var(--pv-border-soft)] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004D40]/20 focus:border-[#1B7566] bg-white appearance-none cursor-pointer transition-all duration-300"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239333ea'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
              >
                <option value="">All Plans</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="px-5 py-4 text-[#004D40] hover:text-[#003930] bg-[var(--pv-color-surface-sunken)] hover:bg-[#DDEEEB] rounded-2xl transition-all duration-300 font-medium"
              >
                <i className="ri-close-line mr-1"></i>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-5 bg-rose-50 border-2 border-rose-200 rounded-2xl text-rose-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <i className="ri-error-warning-fill text-xl"></i>
              {error}
            </span>
            <button onClick={clearError} className="p-2 hover:bg-rose-100 rounded-xl transition-colors">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-3xl shadow-xl shadow-[#004D40]/5 border border-[var(--pv-border-soft)]/50 overflow-hidden">
          {loading ? (
            <div className="p-8">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-[var(--pv-color-surface-sunken)]/50 animate-pulse">
                    <div className="w-12 h-12 bg-[#DDEEEB] rounded-2xl"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-[#DDEEEB] rounded-full w-1/4"></div>
                      <div className="h-3 bg-[var(--pv-color-surface-sunken)] rounded-full w-1/3"></div>
                    </div>
                    <div className="h-8 bg-[#DDEEEB] rounded-full w-24"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-20 h-20 bg-[var(--pv-color-surface-sunken)] rounded-3xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-search-line text-4xl text-[#1B7566]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No users found</h3>
              <p className="text-gray-500">
                {hasFilters ? 'Try adjusting your filters' : 'No users have registered yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--pv-color-surface-sunken)] border-b-2 border-[var(--pv-border-soft)]">
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      User
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Role
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      FAL Status
                    </th>
                    <th className="text-left px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="text-right px-6 py-5 text-xs font-bold text-[#003930] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F2F8F7]">
                  {users.map((user) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      onViewDetails={() => setSelectedUser(user)}
                      onApproveFal={handleApprove}
                      onRejectFal={(u) => {
                        setRejectDialogUser(u);
                        setRejectReason('');
                      }}
                      onRevokeFal={handleRevoke}
                      falLoading={falLoading}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-5 bg-[var(--pv-color-surface-sunken)]/50 border-t-2 border-[var(--pv-border-soft)] flex items-center justify-between">
              <div className="text-sm text-[#004D40] font-medium">
                Showing {((currentPage - 1) * pagination.pageSize) + 1} to{' '}
                {Math.min(currentPage * pagination.pageSize, pagination.total)} of{' '}
                {pagination.total} users
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center border-2 border-[var(--pv-border-soft)] rounded-xl hover:bg-[var(--pv-color-surface-sunken)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <i className="ri-arrow-left-s-line text-[#004D40]"></i>
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 flex items-center justify-center rounded-xl font-medium transition-all duration-300 ${
                        currentPage === page
                          ? 'bg-[#004D40] text-white shadow-lg shadow-[#004D40]/30'
                          : 'border-2 border-[var(--pv-border-soft)] text-[#004D40] hover:bg-[var(--pv-color-surface-sunken)]'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {pagination.totalPages > 5 && <span className="text-[#1B7566] px-2">...</span>}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className="w-10 h-10 flex items-center justify-center border-2 border-[var(--pv-border-soft)] rounded-xl hover:bg-[var(--pv-color-surface-sunken)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <i className="ri-arrow-right-s-line text-[#004D40]"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* User Details Modal */}
      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          plans={plans}
          onClose={() => setSelectedUser(null)}
          onGrantSubscription={grantSubscription}
          onUpdateSubscription={updateSubscription}
          onCancelSubscription={cancelSubscription}
          onRefresh={() => fetchUsers({ page: 1, pageSize: 20 })}
        />
      )}

      {/* FAL Rejection Reason Dialog */}
      {rejectDialogUser && (
        <FalRejectDialog
          user={rejectDialogUser}
          reason={rejectReason}
          onReasonChange={setRejectReason}
          onConfirm={handleRejectConfirm}
          onCancel={() => {
            setRejectDialogUser(null);
            setRejectReason('');
          }}
          isLoading={falLoading === rejectDialogUser.agentId}
        />
      )}
    </div>
  );
}

// ============================================================================
// FAL STATUS BADGE
// ============================================================================

function FalStatusBadge({ status }: { status?: 'PENDING' | 'APPROVED' | 'REJECTED' }) {
  if (!status) return null;

  const labelMap = {
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };

  const iconMap = {
    PENDING: 'ri-time-line',
    APPROVED: 'ri-shield-check-fill',
    REJECTED: 'ri-close-circle-fill',
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${getFalBadgeClasses(status)}`}>
      <i className={iconMap[status]}></i>
      {labelMap[status]}
    </span>
  );
}

// ============================================================================
// FAL REJECT DIALOG
// ============================================================================

interface FalRejectDialogProps {
  user: AdminUser;
  reason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function FalRejectDialog({ user, reason, onReasonChange, onConfirm, onCancel, isLoading }: FalRejectDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="p-6 bg-rose-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <i className="ri-close-circle-line text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Reject FAL License</h2>
                <p className="text-rose-200 text-sm">{getFullName(user)}</p>
              </div>
            </div>
            <button onClick={onCancel} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
              <i className="ri-close-line text-white text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* License info */}
          {user.licenseNumber && (
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">FAL License #</p>
              <p className="font-semibold text-gray-900">{user.licenseNumber}</p>
            </div>
          )}

          {/* Reason input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rejection Reason <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Explain why the FAL license is being rejected..."
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none transition-all"
            />
            {!reason.trim() && (
              <p className="mt-1 text-xs text-gray-400">A reason is required to reject the license.</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!reason.trim() || isLoading}
              className="flex-1 py-3 bg-rose-600 text-white rounded-xl font-medium hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? 'Rejecting...' : 'Confirm Rejection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SUBSCRIPTION BADGE
// ============================================================================

function SubscriptionBadge({ plan, billingCycle }: { plan?: { name: string; slug: string }; billingCycle?: string }) {
  if (!plan) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
        <i className="ri-close-circle-line"></i>
        No Plan
      </span>
    );
  }

  const colors = getTierColors(plan.slug);

  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} border border-current/20`}>
        <i className="ri-vip-crown-2-fill"></i>
        {plan.name}
      </span>
      {billingCycle && (
        <span className="text-xs text-gray-400 pl-1">
          {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// USER ROW
// ============================================================================

interface UserRowProps {
  user: AdminUser;
  onViewDetails: () => void;
  onApproveFal: (user: AdminUser) => void;
  onRejectFal: (user: AdminUser) => void;
  onRevokeFal: (user: AdminUser) => void;
  falLoading: string | null;
}

function UserRow({ user, onViewDetails, onApproveFal, onRejectFal, onRevokeFal, falLoading }: UserRowProps) {
  const subscription = user.subscription;
  const plan = subscription?.plan;
  const isFalUser = user.accountType === 'broker' || user.accountType === 'company';
  const isUpdating = falLoading === user.agentId;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
      case 'ADMIN':
        return { bg: 'bg-rose-100', text: 'text-rose-700', icon: 'ri-shield-star-fill' };
      case 'AGENT':
        return { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'ri-briefcase-fill' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'ri-user-fill' };
    }
  };

  const roleBadge = getRoleBadge(user.role);

  return (
    <tr className="hover:bg-[var(--pv-color-surface-sunken)]/50 transition-all duration-300 group">
      <td className="px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#004D40] rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-[#004D40]/20 group-hover:scale-105 transition-transform duration-300">
            {(user.firstName?.[0] || user.email[0]).toUpperCase()}
          </div>
          <div>
            <div className="font-semibold text-gray-900">{getFullName(user)}</div>
            <div className="text-sm text-gray-500">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-5">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${roleBadge.bg} ${roleBadge.text}`}>
          <i className={roleBadge.icon}></i>
          {user.role}
        </span>
      </td>
      <td className="px-6 py-5">
        <SubscriptionBadge plan={plan} billingCycle={subscription?.billingCycle} />
      </td>
      <td className="px-6 py-5">
        {subscription ? (
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${getStatusColor(subscription.status)}`}>
            {subscription.status === 'ACTIVE' && <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>}
            {subscription.status}
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200"></span>
        )}
      </td>
      {/* FAL Status Column */}
      <td className="px-6 py-5">
        {isFalUser ? (
          <div className="flex flex-col gap-2 min-w-[200px]">
            {/* License Number */}
            <div className="text-xs text-gray-500">
              {user.licenseNumber
                ? <span className="font-mono font-medium text-gray-700">{user.licenseNumber}</span>
                : <span className="italic text-gray-400">No license submitted</span>
              }
            </div>
            {/* FAL Status Badge */}
            <FalStatusBadge status={user.falVerificationStatus ?? 'PENDING'} />
            {/* Action Buttons */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {isUpdating ? (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Updating...
                </span>
              ) : (
                <>
                  {(user.falVerificationStatus === 'PENDING' || !user.falVerificationStatus) && (
                    <>
                      <button
                        onClick={() => onApproveFal(user)}
                        disabled={!user.agentId}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Approve FAL license"
                      >
                        <i className="ri-check-line"></i>
                        Approve
                      </button>
                      <button
                        onClick={() => onRejectFal(user)}
                        disabled={!user.agentId}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Reject FAL license"
                      >
                        <i className="ri-close-line"></i>
                        Reject
                      </button>
                    </>
                  )}
                  {user.falVerificationStatus === 'APPROVED' && (
                    <button
                      onClick={() => onRevokeFal(user)}
                      disabled={!user.agentId}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Revoke FAL approval (set to pending)"
                    >
                      <i className="ri-refresh-line"></i>
                      Revoke
                    </button>
                  )}
                  {user.falVerificationStatus === 'REJECTED' && (
                    <button
                      onClick={() => onApproveFal(user)}
                      disabled={!user.agentId}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Approve FAL license"
                    >
                      <i className="ri-check-line"></i>
                      Approve
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <span className="text-xs text-gray-400 italic">N/A</span>
        )}
      </td>
      <td className="px-6 py-5 text-sm text-gray-500 font-medium">
        {formatDate(user.createdAt)}
      </td>
      <td className="px-6 py-5 text-right">
        <button
          onClick={onViewDetails}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--pv-color-surface-sunken)] hover:bg-[#DDEEEB] text-[#003930] rounded-xl transition-all duration-300 font-medium text-sm hover:scale-105"
        >
          <i className="ri-eye-line"></i>
          View
        </button>
      </td>
    </tr>
  );
}

// ============================================================================
// USER DETAILS MODAL
// ============================================================================

interface UserDetailsModalProps {
  user: AdminUser;
  plans: { id: string; name: string; slug: string }[];
  onClose: () => void;
  onGrantSubscription: (userId: string, planId: string, billingCycle: string) => Promise<boolean>;
  onUpdateSubscription: (userId: string, data: { planId?: string; billingCycle?: string }) => Promise<boolean>;
  onCancelSubscription: (userId: string, reason?: string) => Promise<boolean>;
  onRefresh: () => void;
}

function UserDetailsModal({
  user,
  plans,
  onClose,
  onGrantSubscription,
  onUpdateSubscription,
  onCancelSubscription,
  onRefresh
}: UserDetailsModalProps) {
  const subscription = user.subscription;
  const plan = subscription?.plan;
  const tierColors = getTierColors(plan?.slug);

  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(plan?.id || '');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(subscription?.billingCycle || 'monthly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleGrantOrUpdate = async () => {
    if (!selectedPlanId) return;

    setIsSubmitting(true);
    try {
      let success = false;
      if (subscription && subscription.status === 'ACTIVE') {
        // Update existing subscription
        success = await onUpdateSubscription(user.id, { planId: selectedPlanId, billingCycle: selectedBillingCycle });
      } else {
        // Grant new subscription
        success = await onGrantSubscription(user.id, selectedPlanId, selectedBillingCycle);
      }
      if (success) {
        setShowSubscriptionForm(false);
        onRefresh();
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setIsSubmitting(true);
    try {
      const success = await onCancelSubscription(user.id);
      if (success) {
        setShowCancelConfirm(false);
        onRefresh();
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[90vh] overflow-y-auto">
        {/* Header: solid teal; the plan shows as a chip below */}
        <div className={`p-6 ${plan ? 'bg-[#004D40]' : 'bg-gray-700'}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-2xl">
                {(user.firstName?.[0] || user.email[0]).toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{getFullName(user)}</h2>
                <p className="text-white/80 text-sm">{user.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <i className="ri-close-line text-white text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* FAL Section - Only for brokers/companies */}
          {(user.accountType === 'broker' || user.accountType === 'company') && (
            <div className="bg-amber-50 rounded-2xl p-5 border-2 border-amber-200">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                <i className="ri-shield-star-line text-amber-600"></i>
                FAL License Verification
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm">License #</span>
                  <span className="font-mono font-medium text-gray-900 text-sm">
                    {user.licenseNumber || <em className="text-gray-400 font-normal">Not submitted</em>}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm">Status</span>
                  <FalStatusBadge status={user.falVerificationStatus ?? 'PENDING'} />
                </div>
                {user.falRejectionReason && (
                  <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-xl">
                    <p className="text-xs text-rose-600 font-medium mb-1">Rejection reason:</p>
                    <p className="text-xs text-rose-700">{user.falRejectionReason}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Subscription Section */}
          <div className="bg-[var(--pv-color-surface-sunken)] rounded-2xl p-5 border-2 border-[var(--pv-border-soft)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#003930] flex items-center gap-2">
                <i className="ri-vip-crown-2-fill text-[#004D40]"></i>
                Subscription
              </h3>
              {!showSubscriptionForm && (
                <button
                  onClick={() => setShowSubscriptionForm(true)}
                  className="text-sm px-3 py-1.5 bg-[#004D40] text-white rounded-lg hover:bg-[#003930] transition-colors"
                >
                  {plan ? 'Change Plan' : 'Add Plan'}
                </button>
              )}
            </div>

            {showSubscriptionForm ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Plan</label>
                  <select
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-[var(--pv-border-soft)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-[#004D40]"
                  >
                    <option value="">Select a plan...</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Billing Cycle</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedBillingCycle('monthly')}
                      className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                        selectedBillingCycle === 'monthly'
                          ? 'bg-[#004D40] text-white'
                          : 'bg-[var(--pv-color-surface-sunken)] text-[#003930] hover:bg-[#DDEEEB]'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setSelectedBillingCycle('yearly')}
                      className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                        selectedBillingCycle === 'yearly'
                          ? 'bg-[#004D40] text-white'
                          : 'bg-[var(--pv-color-surface-sunken)] text-[#003930] hover:bg-[#DDEEEB]'
                      }`}
                    >
                      Yearly
                    </button>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowSubscriptionForm(false)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGrantOrUpdate}
                    disabled={!selectedPlanId || isSubmitting}
                    className="flex-1 py-3 bg-[#004D40] text-white rounded-xl font-medium hover:bg-[#003930] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isSubmitting ? 'Saving...' : plan ? 'Update' : 'Grant'}
                  </button>
                </div>
              </div>
            ) : plan ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Plan</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${tierColors.bg} ${tierColors.text}`}>
                    {plan.name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Billing</span>
                  <span className="font-medium text-gray-900">
                    {subscription?.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(subscription?.status || '')}`}>
                    {subscription?.status}
                  </span>
                </div>
                {subscription?.currentPeriodEnd && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Renews</span>
                    <span className="font-medium text-gray-900">
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {subscription?.status === 'ACTIVE' && (
                  <div className="pt-3 border-t border-[var(--pv-border-soft)]">
                    {showCancelConfirm ? (
                      <div className="space-y-3">
                        <p className="text-sm text-rose-600 font-medium">Are you sure you want to cancel this subscription?</p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowCancelConfirm(false)}
                            className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all text-sm"
                          >
                            No, Keep It
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="flex-1 py-2 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50 transition-all text-sm"
                          >
                            {isSubmitting ? 'Canceling...' : 'Yes, Cancel'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowCancelConfirm(true)}
                        className="w-full py-2 text-rose-600 hover:bg-rose-50 rounded-lg font-medium transition-all text-sm"
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No active subscription</p>
            )}
          </div>

          {/* User Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Role</p>
              <p className="font-semibold text-gray-900">{user.role}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
              <p className="font-semibold text-gray-900">{user.status}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Joined</p>
              <p className="font-semibold text-gray-900 text-sm">{formatDate(user.createdAt)}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Login</p>
              <p className="font-semibold text-gray-900 text-sm">
                {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-all duration-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
